import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Link,
  Tooltip,
  Stack,
  Typography,
  useTheme,
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  CardHeader,
  Avatar,
  Pagination,
  useMediaQuery,
  tableCellClasses,
  styled,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchGet } from '../../api/common';
import { track } from '../../api/track';
import {
  CATEGORY_SETS_LABEL,
  MODE_ABBR,
  MODE_DISPLAY_NAME,
} from '../../common/constants';
import { ScoreCardModal } from '../../modals/ScoreCardModal';
import { calcMatchRating } from '../../services/mmr-calc';
import { Player, Mode, DetailedPlayer, DbQuestionData } from '../room/types';
import { assignRanks, utcToLocalDateTime } from '../room/utils';
import DoneIcon from '@mui/icons-material/Done';
import { BiGhost } from 'react-icons/bi';
import Countdown, { zeroPad } from 'react-countdown';
import { JoinedPlayersMorePopover } from '../main/JoinedPlayersMorePopover';
import { Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';

const TableCellTourneyMessage = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    border: 'none',
    padding: 0,
  },
}));

type PropTypes = {
  handleForceStartClick: (game: Game) => void;
  handleJoinGameClick: (gameId: string, mode: Mode) => void;
  handleLeaveGameClick: (gameId: string, profileId: string) => void;
  handleRemoveGameClick: (game: Game) => void;
  handleSpectateClick: (gameId: string) => void;
  lobbyGames?: Game[];
  profile: Profile;
};

export const TournamentLobbyTable = ({
  handleForceStartClick,
  handleJoinGameClick,
  handleLeaveGameClick,
  handleRemoveGameClick,
  handleSpectateClick,
  lobbyGames,
  profile: profileMe,
}: PropTypes) => {
  const navigate = useNavigate();
  const [openScorecardModal, setOpenScorecardModal] = useState(false);
  const [gameResults, setGameResults] = useState<
    Game & { questionSet: DbQuestionData[] }
  >();
  const [pageNum, setPageNum] = useState(1);
  const [lobbyGamesPaged, setLobbyGamesPaged] = useState<Game[]>();
  const [noGamesToDisplay, setNoGamesToDisplay] = useState(false);
  const [clickedJoinedPlayersMore, setClickedJoinedPlayersMore] =
    useState<any>();
  const [openJoinedPlayersMorePopover, setOpenJoinedPlayersMorePopover] =
    useState(false);
  const [anchorElJoinedPlayersMore, setAnchorElJoinedPlayersMore] =
    useState<HTMLButtonElement | null>(null);

  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  useEffect(() => {
    const start = 10 * pageNum - 10;
    const end = 10 * pageNum;
    setLobbyGamesPaged(lobbyGames?.slice(start, end));
  }, [lobbyGames, pageNum]);

  useEffect(() => {
    if (!lobbyGamesPaged) return;
    if (!lobbyGamesPaged.length) {
      setNoGamesToDisplay(true);
    } else {
      setNoGamesToDisplay(false);
    }
  }, [lobbyGamesPaged]);

  const canJoin = (game: Game, profileId: string, isAdmin: boolean) => {
    if (isAdmin || !game.whoCanJoin || game.whoCanJoin.length === 0)
      return true;
    if (game.whoCanJoin?.length > 0 && game.whoCanJoin?.includes(profileId))
      return true;
    return false;
  };

  const actionButton = (r: Game) => {
    if (r.gameOver) return null;
    if (!r.gameStarted && r.confirmingPlayers) {
      return (
        <Typography variant="subtitle2">
          Spectate will be available once game starts
        </Typography>
      );
    }
    if (
      r.mode === 'match' &&
      !r.activePlayers.includes(profileMe.id) &&
      r.activePlayers.length !== 2 &&
      canJoin(r, profileMe.id, profileMe.admin)
    ) {
      return (
        <Stack direction={'row'} spacing={1}>
          <Button
            variant="contained"
            sx={{ width: 80 }}
            onClick={() => handleJoinGameClick(r.gameId, r.mode)}
          >
            Join
          </Button>
          {r.createdBy === profileMe.id ? (
            <Button
              variant="contained"
              sx={{ width: 80 }}
              onClick={() => handleRemoveGameClick(r)}
            >
              Remove
            </Button>
          ) : null}
        </Stack>
      );
    }
    if (
      r.mode === 'sit-n-go' &&
      !r.gameStarted &&
      !r.activePlayers.includes(profileMe.id) &&
      r.status !== 'Starting' &&
      r.activePlayers.length < r.sitAndGoMaxPlayers &&
      canJoin(r, profileMe.id, profileMe.admin)
    ) {
      return (
        <Stack direction={'row'} spacing={1}>
          <Button
            variant="contained"
            sx={{ width: 80 }}
            onClick={() => handleJoinGameClick(r.gameId, r.mode)}
          >
            Join
          </Button>
          {r.activePlayers.length === 0 && r.createdBy === profileMe.id ? (
            <Button
              sx={{ width: 80 }}
              variant="contained"
              onClick={() => handleRemoveGameClick(r)}
            >
              Remove
            </Button>
          ) : null}
          {r.mode === 'sit-n-go' &&
          r.activePlayers.length > 1 &&
          profileMe.admin ? (
            <Tooltip
              placement="top"
              title="Start early with currently joined players"
            >
              <Button
                variant="contained"
                sx={{ width: 80 }}
                onClick={() => handleForceStartClick(r)}
              >
                Force Start
              </Button>
            </Tooltip>
          ) : null}
        </Stack>
      );
    }
    if (
      (r.mode === 'match' || r.mode === 'sit-n-go') &&
      (r.activePlayers.includes(profileMe.id) || profileMe.admin) &&
      !r.gameStarted &&
      canJoin(r, profileMe.id, profileMe.admin)
    ) {
      return (
        <Stack direction={'row'} spacing={1}>
          <Button
            variant="contained"
            onClick={() => handleLeaveGameClick(r.gameId, profileMe.id)}
          >
            Leave Game
          </Button>
          {r.mode === 'sit-n-go' &&
          r.activePlayers.length > 1 &&
          (r.createdBy === profileMe.id || profileMe.admin) ? (
            <Tooltip
              placement="top"
              title="Start early with currently joined players"
            >
              <Button
                variant="contained"
                onClick={() => handleForceStartClick(r)}
              >
                Force Start
              </Button>
            </Tooltip>
          ) : null}
        </Stack>
      );
    }
    if (
      (r.mode === 'solo' || r.mode === 'match' || r.mode === 'sit-n-go') &&
      r.gameStarted &&
      !r.gameOver &&
      !r.activePlayers.find((profileId: string) => profileId === profileMe.id)
    ) {
      if (!profileMe.admin && r.tournament && !r.allowSpectators) {
        return (
          <Typography variant="subtitle2">Spectate on Twitch Stream</Typography>
        );
      } else {
        return (
          <Button
            variant="contained"
            sx={{
              backgroundColor: 'secondary.main',
              color: 'secondary.contrastText',
            }}
            onClick={() => handleSpectateClick(r.gameId)}
          >
            Spectate
          </Button>
        );
      }
    }
    return (
      <Typography variant="subtitle2">
        Spectate will be available once game starts
      </Typography>
    );
  };

  const handleUsernameClick = async (e: any, player: Player) => {
    e.stopPropagation();
    await track(profileMe.id, 'lobby.username.link', player.username);
    navigate(`/profile/overview/${player.profileId}`);
  };

  const getRatingNextToUsername = (
    mode: Mode,
    gameOver: boolean,
    player: Player
  ) => {
    const formatRating = (rating: number) => {
      const rounded = Math.floor(rating);
      return `${rounded > 0 ? '+' : ''}${rounded}`;
    };

    if (gameOver) {
      return formatRating(player.rankedRatingDiff || 0);
    }
    return (player.rankedMatchGamesPlayed || 0) +
      (player.rankedSitAndGoGamesPlayed || 0) <
      10
      ? 'calibrating'
      : player.rankedRating;
  };

  const ratingColor = (gameOver: boolean, player: Player) => {
    if (gameOver) {
      if (Math.round(player.rankedRatingDiff || 0) > 0)
        return theme.palette.success.main;
      if (Math.round(player.rankedRatingDiff || 0) < 0)
        return theme.palette.error.main;
      return theme.palette.warning.main;
    }
    return theme.palette.ranked.main;
  };

  const getJoinedPlayers = (game: Game) => {
    if (!game.players || Object.keys(game.players).length === 0) return '--';
    const rankedPlayers = assignRanks(
      Object.values(game.players).sort((a, b) => a.parScore - b.parScore)
    );
    return (
      <Stack width={'100%'}>
        {rankedPlayers.slice(0, 20).map((p, i) => (
          <Link
            key={i}
            component="button"
            variant="body2"
            onClick={(e) => handleUsernameClick(e, p)}
            textAlign={'left'}
          >
            <Stack direction={'row'} sx={{ whiteSpace: 'nowrap' }}>
              {game.mode === 'sit-n-go'
                ? `${game.gameOver ? p.rank : i + 1}.`
                : ''}{' '}
              {p.username}
              {(game.mode === 'match' || game.mode === 'sit-n-go') &&
              game.ranked ? (
                <Stack direction={'row'}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    &nbsp;(
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: ratingColor(game.gameOver, p) }}
                  >
                    {getRatingNextToUsername(game.mode, game.gameOver, p)}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    )
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          </Link>
        ))}
        {rankedPlayers.length > 20 ? (
          <Link
            component="button"
            variant="body1"
            onClick={(e) =>
              handleJoinPlayersMore(
                e,
                rankedPlayers,
                game.gameOver,
                game.ranked
              )
            }
            textAlign={'left'}
          >
            <Stack
              direction={'row'}
              sx={{ whiteSpace: 'nowrap', color: theme.palette.primary.main }}
            >
              ...{rankedPlayers.length - 20} more players
            </Stack>
          </Link>
        ) : null}
      </Stack>
      //   <Tooltip placement="top" title={mmrCalc(game.players)}>
      //     <Chip
      //       label="MMR"
      //       sx={{ textAlign: 'right', color: theme.palette.ranked.main  }}
      //     ></Chip>
      //   </Tooltip>
      // </Stack>
    );
  };

  const handleJoinPlayersMore = (
    event: any,
    players: Player[],
    gameOver: boolean,
    ranked: boolean
  ) => {
    setClickedJoinedPlayersMore({ players, gameOver, ranked });
    setAnchorElJoinedPlayersMore(event.currentTarget);
    setOpenJoinedPlayersMorePopover(true);
  };

  const mmrCalc = (players: Record<string, Player>) => {
    const otherPlayerRating = getOtherPlayerMatchRating(players);
    if (!otherPlayerRating) return 'N/A';
    return (
      <div>
        {calcMatchRating(
          'win',
          profileMe.rankedMatchGamesPlayed,
          profileMe.rankedMatchRating,
          otherPlayerRating
        )}
      </div>
    );
  };

  const getOtherPlayerMatchRating = (players: Record<string, Player>) => {
    const otherPlayerProfileId = Object.keys(players).find(
      (pid) => pid !== profileMe.id
    );
    if (!otherPlayerProfileId) return undefined;
    return players[otherPlayerProfileId].rankedMatchRating;
  };

  const isJoined = (game: Game) => {
    if (!game.activePlayers) return false;
    return (
      Object.keys(game.activePlayers).includes(profileMe.id) && !game.gameOver
    );
  };

  const handleViewResultsClick = async (game: Game) => {
    const res = await fetchGet(`/games/result/${game.gameId}`);
    res.game.questionSet = res.questionSet;
    setGameResults(res.game);
    setOpenScorecardModal(true);
  };

  const results = (game: Game) => (
    <Stack>
      <Typography variant="body2">
        {utcToLocalDateTime(game.createdAt)}
      </Typography>
      <Link
        sx={{ cursor: 'pointer' }}
        onClick={() => handleViewResultsClick(game)}
      >
        View Results
      </Link>
    </Stack>
  );

  const stackName = (r: Game) => (
    <Tooltip placement="top" title={`Game ID: ${r.gameId}`}>
      <Stack>
        <Stack direction="row" spacing={1}>
          {r.ranked ? (
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: theme.palette.ranked.main,
              }}
            >
              Ranked
            </Typography>
          ) : null}
          {r.ghostChallenge ? (
            <BiGhost
              style={{
                fontSize: '18px',
                color: theme.palette.warning.main,
              }}
            />
          ) : null}
        </Stack>
        {r.customGameName || 'N/A'}
      </Stack>
    </Tooltip>
  );

  const gameType = (r: Game) =>
    r.demo
      ? '--'
      : r.gameType === 'casual'
      ? 'Cas.'
      : r.gameType === 'pro'
      ? 'Pro'
      : r.gameType;

  const categoryAndDemo = (r: Game) =>
    r.demo || r.categorySet === 'Demo'
      ? 'Demo'
      : CATEGORY_SETS_LABEL[r.categorySet];

  const handlePaginateOnChange = (event: any, pageNum: number) => {
    setPageNum(pageNum);
  };

  const countdownRenderer = ({ minutes, seconds }: any) => {
    return (
      <span>
        {zeroPad(minutes)}:{zeroPad(seconds)}
      </span>
    );
  };

  const getStatus = (r: Game) => {
    if (
      r.mode === 'sit-n-go' &&
      !r.gameStarted &&
      r.activePlayers &&
      !r.confirmingPlayers
    ) {
      return `${r.activePlayers.length} / ${r.sitAndGoMaxPlayers}`;
    }
    if (!r.gameStarted && r.confirmingPlayers) {
      return 'Starting';
    }
    if (r.gameStarted && !r.gameOver) {
      return 'In progress';
    }
    return r.status;
  };

  const pagination = (
    <Pagination
      count={lobbyGames ? Math.ceil(lobbyGames.length / 10) : 10}
      size={xsBreakpoint ? 'small' : 'medium'}
      onChange={handlePaginateOnChange}
      sx={{ display: 'flex', justifyContent: 'right' }}
    />
  );

  const lobbyCards = (
    <Grid container spacing={1}>
      {!noGamesToDisplay ? (
        lobbyGamesPaged?.map((r: Game) => (
          <Grid item key={r.gameId} xs={6}>
            <Card
              variant="outlined"
              // key={r.gameId}
              sx={{
                border: isJoined(r) ? '2px solid' : 'none',
                borderColor: isJoined(r) ? 'secondary.main' : 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              {isJoined(r) ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 12,
                    padding: '2px',
                    float: 'right',
                    backgroundColor: 'secondary.main',
                    color: 'secondary.contrastText',
                  }}
                >
                  Joined
                </Typography>
              ) : null}
              <CardHeader
                avatar={
                  r.status === 'Finished' ? (
                    <Avatar
                      sx={{
                        backgroundColor: theme.palette.tertiary.main,
                      }}
                    >
                      <DoneIcon
                        sx={{
                          color: 'success.main',
                          textShadow: '0 0 18px #fff, 0 0 17px #fff',
                        }}
                      />
                    </Avatar>
                  ) : null
                }
                title={stackName(r)}
              />
              <CardContent sx={{ pt: 0 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  color="text.secondary"
                >
                  <Typography variant="overline">
                    {MODE_ABBR[r.mode]}
                  </Typography>
                  <Typography variant="overline"> | </Typography>
                  <Typography variant="overline">{gameType(r)}</Typography>
                  <Typography variant="overline"> | </Typography>
                  <Typography variant="overline">
                    {categoryAndDemo(r)}
                  </Typography>
                </Stack>
                {getJoinedPlayers(r)}
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                {r.status === 'Finished' ? results(r) : actionButton(r)}
              </CardActions>
            </Card>
          </Grid>
        ))
      ) : (
        <Card>
          <CardContent style={{ textAlign: 'center' }}>
            No games to display
          </CardContent>
        </Card>
      )}
    </Grid>
  );

  const lobbyTable = (
    <TableContainer component={Paper}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell width={'15%'}>Name</TableCell>
            <TableCell width={'5%'}>Mode</TableCell>
            <TableCell width={'7%'}>
              <Tooltip placement="top" title="Difficulty">
                <span>Diff.</span>
              </Tooltip>
            </TableCell>
            <TableCell width={'10%'}>Category</TableCell>
            <TableCell width={'20%'}>Players</TableCell>
            <TableCell width={'10%'}>Status</TableCell>
            <TableCell width={'15%'}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!noGamesToDisplay ? (
            lobbyGamesPaged?.map((r: Game) => (
              <Box key={r.gameId} width="100%" display="contents">
                {isJoined(r) ? (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      position: 'absolute',
                      backgroundColor: 'secondary.main',
                      color: 'secondary.contrastText',
                    }}
                  >
                    Joined
                  </Typography>
                ) : null}
                {r.tournament &&
                r.joinGameTimerUntil &&
                !r.autoStartIn &&
                !r.gameStarted ? (
                  <TableRow>
                    <TableCellTourneyMessage colSpan={7}>
                      <Box
                        sx={{
                          paddingTop: 1,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          textAlign="center"
                          sx={{
                            paddingLeft: 1,
                            paddingRight: 1,
                            border: '1px solid',
                            borderColor: theme.palette.warning.main,
                            borderRadius: '5px',
                          }}
                        >
                          Time left to join:{' '}
                          <Countdown
                            date={r.joinGameTimerUntil}
                            renderer={countdownRenderer}
                          />
                        </Typography>
                      </Box>
                    </TableCellTourneyMessage>
                  </TableRow>
                ) : null}
                {r.tournament && r.autoStartIn && !r.gameStarted ? (
                  <TableRow>
                    <TableCellTourneyMessage colSpan={7}>
                      <Box
                        sx={{
                          paddingTop: 1,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          textAlign="center"
                          sx={{
                            paddingLeft: 1,
                            paddingRight: 1,
                            border: '1px solid',
                            borderColor: theme.palette.underPar.main,
                            borderRadius: '5px',
                          }}
                        >
                          Game starting in:{' '}
                          <Countdown
                            date={r.autoStartIn}
                            renderer={countdownRenderer}
                          />
                        </Typography>
                      </Box>
                    </TableCellTourneyMessage>
                  </TableRow>
                ) : null}
                <TableRow
                  sx={{
                    border: isJoined(r) ? '2px solid' : 'none',
                    borderColor: isJoined(r) ? 'secondary.main' : 'none',
                  }}
                >
                  <TableCell>{stackName(r)}</TableCell>
                  <TableCell>
                    <Tooltip
                      placement="top-end"
                      title={MODE_DISPLAY_NAME[r.mode] || ''}
                    >
                      <span>{MODE_ABBR[r.mode]}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{gameType(r)}</TableCell>
                  <TableCell>{categoryAndDemo(r)}</TableCell>
                  <TableCell>{getJoinedPlayers(r)}</TableCell>
                  <TableCell>{getStatus(r)}</TableCell>
                  <TableCell>
                    {r.status === 'Finished' ? results(r) : actionButton(r)}
                  </TableCell>
                </TableRow>
              </Box>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} style={{ textAlign: 'center' }}>
                No games to display
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      {clickedJoinedPlayersMore ? (
        <JoinedPlayersMorePopover
          anchorEl={anchorElJoinedPlayersMore}
          close={() => setOpenJoinedPlayersMorePopover(false)}
          gameOver={clickedJoinedPlayersMore.gameOver}
          handleUsernameClick={handleUsernameClick}
          open={openJoinedPlayersMorePopover}
          players={clickedJoinedPlayersMore.players}
          ranked={clickedJoinedPlayersMore.ranked}
        />
      ) : null}
      {gameResults && profileMe.id ? (
        <ScoreCardModal
          onClose={() => setOpenScorecardModal(false)}
          categorySet={gameResults.categorySet}
          gameId={gameResults.gameId}
          gameType={gameResults.gameType}
          openScordcardModal={openScorecardModal}
          mode={gameResults.mode}
          players={gameResults.players as Record<string, DetailedPlayer>}
          profileId={
            profileMe
              ? profileMe.id
              : Object.values(gameResults.players)[0].profileId
          }
          questionSet={gameResults.questionSet}
          ranked={gameResults.ranked}
        ></ScoreCardModal>
      ) : null}
      <Stack direction={'row'} mb={1} justifyContent={'space-between'}>
        <Typography
          variant="subtitle2"
          textAlign={'left'}
          color="warning.light"
        >
          Please join the
          <Link
            sx={{ cursor: 'pointer', marginLeft: 1, marginRight: 1 }}
            href="https://discord.gg/upX48KPfHd"
            target="_blank"
          >
            Discord
          </Link>
          for tournament communications
        </Typography>
        {pagination}
      </Stack>
      {xsBreakpoint ? lobbyCards : lobbyTable}
    </>
  );
};
